import React, { useState, useEffect } from 'react';
import apiClient from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { FileText, CheckCircle2, GraduationCap, IndianRupee, ShieldAlert, Send, Download, Clock } from 'lucide-react';
import GstBilling from './GstBilling';

const RegistrationAdmissionCard = ({ studentId, user, onConverted }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [raiseOpen, setRaiseOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);

  const isPrivileged = user?.role === 'admin' || user?.role === 'operating_head';

  const load = async () => {
    try {
      const res = await apiClient.get(`/finance/registration-status/${studentId}`);
      setStatus(res.data);
      setAmount(String(res.data?.default_amount ?? 10000));
    } catch { /* non-fatal */ }
    setLoading(false);
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await apiClient.get(`/finance/registration-status/${studentId}`);
        if (active) {
          setStatus(res.data);
          setAmount(String(res.data?.default_amount ?? 10000));
        }
      } catch { /* non-fatal */ }
      if (active) setLoading(false);
    })();
    return () => { active = false; };
  }, [studentId]);

  const raiseInvoice = async () => {
    setBusy(true);
    try {
      await apiClient.post('/finance/registration-invoice', {
        student_id: studentId,
        amount: parseFloat(amount) || undefined,
      });
      toast.success('Registration fee invoice raised');
      setRaiseOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to raise invoice');
    } finally { setBusy(false); }
  };

  const downloadInvoice = async () => {
    try {
      const res = await apiClient.get(`/finance/payments/${status.invoice_id}/invoice-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${status.is_proforma ? 'Proforma' : 'Invoice'}_${status.document_label || status.invoice_number || status.proforma_number || status.invoice_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download invoice');
    }
  };

  const sendLink = async () => {
    setSendingLink(true);
    try {
      const res = await apiClient.post('/finance/registration-invoice/send-link', { student_id: studentId });
      toast.success(res.data?.delivery_path === 'session_text'
        ? 'Payment link sent via WhatsApp (text message)'
        : 'Payment link sent to the student on WhatsApp 📲');
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'object' ? detail.message : (detail || 'Failed to send payment link'));
    } finally { setSendingLink(false); }
  };

  const convert = async (override = false) => {
    if (!override && !window.confirm('Convert this student to Admission? The registration fee is verified.')) return;
    if (override && !window.confirm('Force-convert WITHOUT a verified registration fee? (Admin override)')) return;
    setBusy(true);
    try {
      await apiClient.post(`/students/${studentId}/convert-to-admission`, { override });
      toast.success('Converted to Admission successfully! 🎉');
      onConverted?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'object' ? detail.message : (detail || 'Failed to convert'));
    } finally { setBusy(false); }
  };

  if (loading) return null;

  const hasInvoice = status?.has_invoice;
  const paid = status?.paid;

  return (
    <Card data-testid="registration-admission-card" className="border-[#0DA891]/30">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <GraduationCap className="w-4 h-4 text-[#0DA891]" />
          Registration &amp; Admission
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-gray-500">
          Raise the registration fee invoice and share it with the student. Accounts verify the payment in Finance, then you can convert to Admission.
        </p>

        {/* GST / Company billing — optional, used on all invoices for this student */}
        <GstBilling studentId={studentId} />

        {!hasInvoice && (
          <Button
            className="w-full h-9 text-xs bg-[#0DA891] hover:bg-[#0b937f]"
            data-testid="raise-registration-invoice-btn"
            onClick={() => setRaiseOpen(true)}
          >
            <FileText className="w-3.5 h-3.5 mr-1.5" /> Raise Proforma Invoice
          </Button>
        )}

        {hasInvoice && (
          <div className="rounded-lg border p-3 space-y-1.5" data-testid="registration-invoice-summary">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-700">{status.document_label || status.invoice_number || status.proforma_number}</span>
              <Badge
                className={paid ? 'bg-emerald-100 text-emerald-700' : status.status === 'partial' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}
                data-testid="registration-invoice-status"
              >
                {status.is_proforma ? 'Proforma' : 'Tax Invoice'} · {paid ? 'Verified' : status.status === 'partial' ? 'Partial' : 'Pending'}
              </Badge>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Amount</span>
              <span className="font-semibold flex items-center"><IndianRupee className="w-3 h-3" />{Number(status.amount).toLocaleString('en-IN')}</span>
            </div>
            {!paid && (
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>Balance</span>
                <span>₹{Number(status.balance).toLocaleString('en-IN')}</span>
              </div>
            )}
          </div>
        )}

        {hasInvoice && (
          <Button
            variant="outline"
            className="w-full h-9 text-xs"
            data-testid="download-registration-invoice-btn"
            onClick={downloadInvoice}
          >
            <Download className="w-3.5 h-3.5 mr-1.5" /> Download {status.is_proforma ? 'Proforma' : 'Invoice'} PDF
          </Button>
        )}

        {hasInvoice && !paid && (
          <Button
            variant="outline"
            className="w-full h-9 text-xs border-green-500/40 text-green-700 hover:bg-green-50"
            data-testid="send-payment-link-whatsapp-btn"
            disabled={sendingLink}
            onClick={sendLink}
          >
            <Send className="w-3.5 h-3.5 mr-1.5" />
            {sendingLink ? 'Sending...' : 'Send Payment Link via WhatsApp'}
          </Button>
        )}

        {hasInvoice && !paid && (
          <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2" data-testid="registration-awaiting-verification">
            <Clock className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Awaiting payment. Accounts will mark it received &amp; verified in the Finance module once paid.</span>
          </div>
        )}

        {paid && (
          <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2" data-testid="registration-paid-banner">
            <CheckCircle2 className="w-4 h-4" /> Registration fee received &amp; verified by Accounts — ready to convert.
          </div>
        )}

        <Button
          className="w-full h-9 text-xs bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
          data-testid="convert-to-admission-btn"
          disabled={busy || (!paid && !isPrivileged)}
          onClick={() => convert(false)}
        >
          <GraduationCap className="w-3.5 h-3.5 mr-1.5" /> Convert to Admission
        </Button>

        {!paid && isPrivileged && (
          <Button
            variant="ghost"
            className="w-full h-8 text-[11px] text-amber-700 hover:bg-amber-50"
            data-testid="force-convert-admin-btn"
            disabled={busy}
            onClick={() => convert(true)}
          >
            <ShieldAlert className="w-3.5 h-3.5 mr-1.5" /> Force Convert (Admin override)
          </Button>
        )}
        {!paid && !isPrivileged && (
          <p className="text-[11px] text-gray-400 text-center">Conversion unlocks once Accounts verify the registration fee.</p>
        )}
      </CardContent>

      {/* Raise invoice dialog */}
      <Dialog open={raiseOpen} onOpenChange={setRaiseOpen}>
        <DialogContent className="max-w-sm" data-testid="raise-invoice-dialog">
          <DialogHeader>
            <DialogTitle>Raise Proforma Invoice (Registration Fee)</DialogTitle>
            <DialogDescription>A Proforma Invoice is generated for the student to pay. The Tax Invoice &amp; Receipt are issued automatically once payment is verified and the student is converted to Admission.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-sm">Amount (₹) — excl. GST</Label>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="registration-amount-input"
            />
            <div className="rounded-md bg-gray-50 border px-3 py-2 text-xs text-gray-600 space-y-0.5">
              <div className="flex justify-between"><span>Base amount</span><span>₹{(parseFloat(amount) || 0).toLocaleString('en-IN')}</span></div>
              <div className="flex justify-between"><span>GST (18%)</span><span>₹{((parseFloat(amount) || 0) * 0.18).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span></div>
              <div className="flex justify-between font-semibold text-gray-800 pt-0.5 border-t mt-0.5"><span>Total payable</span><span>₹{((parseFloat(amount) || 0) * 1.18).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRaiseOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={raiseInvoice} disabled={busy || !amount} data-testid="confirm-raise-invoice-btn" className="bg-[#0DA891] hover:bg-[#0b937f]">
              {busy ? 'Raising...' : 'Raise Invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default RegistrationAdmissionCard;
